if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          current: this.querySelector('[data-gallery-current]'),
          total: this.querySelector('[data-gallery-total]'),
          previous: this.querySelectorAll('[data-gallery-previous]'),
          next: this.querySelectorAll('[data-gallery-next]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.autoplayInterval = Math.max(Number(this.dataset.autoplayInterval) || 5000, 4000);
        this.autoplayTimer = null;
        this.resumeTimer = null;
        this.mediaItems = this.collectMediaItems();

        this.elements.viewer?.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 250));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            ?.addEventListener('click', this.handleThumbnailClick.bind(this, mediaToSwitch.dataset.target));
        });

        this.elements.previous.forEach((button) => button.addEventListener('click', () => this.stepGallery(-1)));
        this.elements.next.forEach((button) => button.addEventListener('click', () => this.stepGallery(1)));
        this.addEventListener('pointerenter', this.pauseAutoplay.bind(this));
        this.addEventListener('pointerleave', this.startAutoplay.bind(this));
        this.addEventListener('pointerdown', () => this.suspendAutoplay());
        this.addEventListener('focusin', this.pauseAutoplay.bind(this));
        this.addEventListener('focusout', (event) => {
          if (!this.contains(event.relatedTarget)) this.scheduleAutoplay(6000);
        });
        document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

        if (this.elements.thumbnails && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }

        this.updateCounter(this.getActiveIndex());
        this.startAutoplay();
      }

      disconnectedCallback() {
        this.pauseAutoplay();
        window.clearTimeout(this.resumeTimer);
      }

      collectMediaItems() {
        const uniqueMedia = new Map();
        this.elements.viewer?.querySelectorAll('li[data-gallery-item][data-gallery-media-id]').forEach((item) => {
          if (!uniqueMedia.has(item.dataset.galleryMediaId)) {
            uniqueMedia.set(item.dataset.galleryMediaId, item);
          }
        });
        return Array.from(uniqueMedia.values());
      }

      handleThumbnailClick(mediaId, event) {
        event.preventDefault();
        this.suspendAutoplay();
        this.setActiveMedia(mediaId, false);
      }

      onSlideChanged(event) {
        const currentElement = event.detail.currentElement?.closest?.('li[data-gallery-item]');
        if (!currentElement || !this.mediaItems.includes(currentElement)) return;
        this.mediaItems.forEach((item) => item.classList.toggle('is-active', item === currentElement));
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateCounter(this.mediaItems.indexOf(currentElement));
        if (this.isAutoplayChange) {
          this.isAutoplayChange = false;
        } else {
          this.suspendAutoplay();
        }
      }

      getActiveIndex() {
        const activeIndex = this.mediaItems.findIndex((item) => item.classList.contains('is-active'));
        return activeIndex >= 0 ? activeIndex : 0;
      }

      stepGallery(direction) {
        if (this.mediaItems.length < 2) return;
        this.suspendAutoplay();
        const nextIndex = (this.getActiveIndex() + direction + this.mediaItems.length) % this.mediaItems.length;
        this.setActiveMedia(this.mediaItems[nextIndex].dataset.mediaId, false);
      }

      updateCounter(index) {
        if (this.elements.current) this.elements.current.textContent = String(index + 1);
        if (this.elements.total) this.elements.total.textContent = String(this.mediaItems.length);
      }

      setActiveMedia(mediaId, prepend) {
        this.mediaItems = this.collectMediaItems();
        const activeMedia = this.mediaItems.find((item) => item.dataset.mediaId === mediaId) || this.mediaItems[0];
        if (!activeMedia) return;

        this.mediaItems.forEach((element) => element.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
          this.mediaItems = this.collectMediaItems();
        }

        window.setTimeout(() => {
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
          }
        });
        this.playActiveMedia(activeMedia);
        this.updateCounter(this.mediaItems.indexOf(activeMedia));

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          window.setTimeout(() => this.elements.liveRegion.setAttribute('aria-hidden', true), 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        activeItem.querySelector('.deferred-media')?.loadContent(false);
      }

      startAutoplay() {
        this.pauseAutoplay();
        if (
          this.dataset.autoplay !== 'true' ||
          this.mediaItems.length < 2 ||
          this.reducedMotion.matches ||
          document.hidden ||
          this.matches(':hover') ||
          this.contains(document.activeElement)
        ) {
          return;
        }

        const imageItems = this.mediaItems.filter((item) => item.dataset.mediaType === 'image');
        if (imageItems.length < 2) return;
        this.autoplayTimer = window.setInterval(() => {
          const activeMedia = this.mediaItems[this.getActiveIndex()];
          if (activeMedia?.dataset.mediaType !== 'image') {
            this.pauseAutoplay();
            return;
          }
          const imageIndex = imageItems.indexOf(activeMedia);
          const nextImage = imageItems[(imageIndex + 1) % imageItems.length];
          this.isAutoplayChange = true;
          this.setActiveMedia(nextImage.dataset.mediaId, false);
          window.setTimeout(() => {
            this.isAutoplayChange = false;
          }, 1000);
        }, this.autoplayInterval);
      }

      pauseAutoplay() {
        if (this.autoplayTimer) window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }

      scheduleAutoplay(delay = 12000) {
        this.pauseAutoplay();
        window.clearTimeout(this.resumeTimer);
        this.resumeTimer = window.setTimeout(() => this.startAutoplay(), delay);
      }

      suspendAutoplay() {
        this.scheduleAutoplay(12000);
      }

      handleVisibilityChange() {
        if (document.hidden) {
          this.pauseAutoplay();
        } else {
          this.scheduleAutoplay(2000);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        this.stickyHeader?.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
